import React, { useState, useEffect } from 'react';
import C from '../../constants/colors';
import * as hseApi from '../../api/hse';

const STATUS_STYLE = {
  PASS:    { bg: C.green + '22',  color: C.green,  label: 'PASS' },
  EXPIRED: { bg: C.red + '22',    color: C.red,    label: 'EXPIRED' },
  PENDING: { bg: C.amber + '22',  color: C.amber,  label: 'PENDING' },
};

function sortRecords(records) {
  const scopeOrder = { ENTERPRISE: 0, SITE: 1 };
  const statusOrder = { PASS: 0, EXPIRED: 1, PENDING: 2 };
  return [...records].sort((a, b) => {
    const scopeDiff = (scopeOrder[a.scope] ?? 1) - (scopeOrder[b.scope] ?? 1);
    if (scopeDiff !== 0) return scopeDiff;
    return (statusOrder[a.effectiveStatus] ?? 2) - (statusOrder[b.effectiveStatus] ?? 2);
  });
}

export default function HseTrainingTab({ employeeId, companyId, siteId }) {
  const [data, setData] = useState({ records: [], notes: [] });
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [noteCourse, setNoteCourse] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (employeeId) {
      hseApi.getEmployeeTraining(employeeId).then(setData).finally(() => setLoading(false));
    }
  }, [employeeId]);

  async function handleMarkPass(record) {
    const updated = await hseApi.updateTrainingRecord(employeeId, record.id, { status: 'PASS' });
    const effective = updated.expiryDate && new Date(updated.expiryDate) < new Date() ? 'EXPIRED' : 'PASS';
    setData(prev => ({
      ...prev,
      records: prev.records.map(r => r.id === updated.id ? { ...updated, effectiveStatus: effective, courseName: r.courseName, scope: r.scope, siteName: r.siteName } : r),
    }));
  }

  async function handleAddNote(e) {
    e.preventDefault();
    if (!noteInput.trim()) return;
    setSavingNote(true);
    try {
      const note = await hseApi.addNote(employeeId, { note: noteInput.trim(), courseId: noteCourse || null });
      const courseMatch = data.records.find(r => r.courseId === noteCourse);
      setData(prev => ({ ...prev, notes: [{ ...note, courseName: courseMatch?.courseName || null }, ...prev.notes] }));
      setNoteInput('');
      setNoteCourse('');
    } finally {
      setSavingNote(false);
    }
  }

  async function handleAssigned() {
    const full = await hseApi.getEmployeeTraining(employeeId);
    setData(full);
    setAssignModal(false);
  }

  if (loading) return <div style={{ padding: '20px', color: C.textMuted, fontSize: '13px' }}>Loading…</div>;

  const sorted = sortRecords(data.records);

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ color: C.textMuted, fontSize: '12px' }}>
          {data.records.length} assigned · {data.records.filter(r => r.effectiveStatus === 'PASS').length} passed · {data.records.filter(r => r.effectiveStatus === 'PENDING').length} pending · {data.records.filter(r => r.effectiveStatus === 'EXPIRED').length} expired
        </div>
        <button
          onClick={() => setAssignModal(true)}
          style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
        >
          + Assign Training
        </button>
      </div>

      {sorted.length === 0 ? (
        <div style={{ color: C.textMuted, fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No trainings assigned yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '20px' }}>
          <thead>
            <tr style={{ color: C.textMuted, borderBottom: `1px solid ${C.border}` }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Subject</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Site</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Status</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Due</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Expiry</th>
              <th style={{ padding: '6px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const s = STATUS_STYLE[r.effectiveStatus] || STATUS_STYLE.PENDING;
              return (
                <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '9px 8px', color: C.textPrimary, fontWeight: 600 }}>{r.courseName}</td>
                  <td style={{ padding: '9px 8px' }}>
                    {r.scope === 'ENTERPRISE'
                      ? <span style={{ background: C.blue + '22', color: C.blue, padding: '1px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>Enterprise</span>
                      : <span style={{ color: C.textMuted, fontSize: '11px' }}>{r.siteName || '—'}</span>
                    }
                  </td>
                  <td style={{ padding: '9px 8px' }}>
                    <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700 }}>{s.label}</span>
                  </td>
                  <td style={{ padding: '9px 8px', color: C.textMuted }}>{r.dueDate ? new Date(r.dueDate).toLocaleDateString() : '—'}</td>
                  <td style={{ padding: '9px 8px', color: r.effectiveStatus === 'EXPIRED' ? C.red : C.textMuted }}>
                    {r.expiryDate ? new Date(r.expiryDate).toLocaleDateString() : '—'}
                    {r.effectiveStatus === 'EXPIRED' && ' ⚠'}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right' }}>
                    {r.effectiveStatus !== 'PASS' && (
                      <button
                        onClick={() => handleMarkPass(r)}
                        style={{ background: C.green + '22', color: C.green, border: `1px solid ${C.green}44`, borderRadius: '5px', padding: '3px 8px', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Mark Passed
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
        <div style={{ fontSize: '11px', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>HSE Notes</div>

        <form onSubmit={handleAddNote} style={{ marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
          <select
            value={noteCourse}
            onChange={e => setNoteCourse(e.target.value)}
            style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '7px 9px', color: C.textPrimary, fontSize: '12px' }}
          >
            <option value="">— General welfare note —</option>
            {data.records.map(r => <option key={r.courseId} value={r.courseId}>{r.courseName}</option>)}
          </select>
          <div style={{ display: 'flex', gap: '7px' }}>
            <input
              value={noteInput}
              onChange={e => setNoteInput(e.target.value)}
              placeholder="Add a note…"
              style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '7px 9px', color: C.textPrimary, fontSize: '12px' }}
            />
            <button
              type="submit"
              disabled={savingNote || !noteInput.trim()}
              style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: savingNote || !noteInput.trim() ? 0.5 : 1 }}
            >
              Save
            </button>
          </div>
        </form>

        {data.notes.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: '12px' }}>No notes yet.</div>
        ) : (
          data.notes.map(n => (
            <div key={n.id} style={{ background: C.surfaceElevated, borderRadius: '7px', padding: '10px 12px', marginBottom: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: C.textPrimary, fontSize: '12px', fontWeight: 600 }}>{n.createdByName}</span>
                <span style={{ color: C.textMuted, fontSize: '10px' }}>{new Date(n.createdAt).toLocaleString()}</span>
              </div>
              <div style={{ color: C.textSecondary, fontSize: '12px', marginBottom: '5px' }}>{n.note}</div>
              {n.courseName
                ? <span style={{ background: C.amber + '22', color: C.amber, padding: '1px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>{n.courseName}</span>
                : <span style={{ background: C.border, color: C.textMuted, padding: '1px 7px', borderRadius: '4px', fontSize: '10px', border: `1px solid ${C.border}` }}>General</span>
              }
            </div>
          ))
        )}
      </div>

      {assignModal && (
        <AssignModal
          employeeId={employeeId}
          companyId={companyId}
          siteId={siteId}
          alreadyAssigned={data.records.map(r => r.courseId)}
          onDone={handleAssigned}
          onClose={() => setAssignModal(false)}
        />
      )}
    </div>
  );
}

function AssignModal({ employeeId, companyId, siteId, alreadyAssigned, onDone, onClose }) {
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (companyId && siteId) {
      hseApi.listCourses(companyId, siteId).then(all => setCourses(all.filter(c => !alreadyAssigned.includes(c.id))));
    }
  }, [companyId, siteId]);

  function toggle(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleAssign() {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      const selectedCourses = courses.filter(c => selected.includes(c.id));
      const siteCourseIds = selectedCourses.filter(c => c.scope === 'SITE').map(c => c.id);
      const entCourseIds  = selectedCourses.filter(c => c.scope === 'ENTERPRISE').map(c => c.id);
      if (siteCourseIds.length > 0) {
        await hseApi.assignTraining(employeeId, { courseIds: siteCourseIds, siteId });
      }
      if (entCourseIds.length > 0) {
        await hseApi.assignTraining(employeeId, { courseIds: entCourseIds, siteId: null });
      }
      onDone();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px', width: '380px', maxHeight: '80vh', overflow: 'auto' }}>
        <h3 style={{ color: C.textPrimary, margin: '0 0 12px', fontSize: '15px' }}>Assign Training</h3>
        {courses.length === 0
          ? <div style={{ color: C.textMuted, fontSize: '13px' }}>No additional courses available.</div>
          : courses.map(c => (
            <div
              key={c.id}
              onClick={() => toggle(c.id)}
              style={{ padding: '10px', borderRadius: '7px', cursor: 'pointer', background: selected.includes(c.id) ? C.blue + '22' : C.surface, border: `1px solid ${selected.includes(c.id) ? C.blue + '44' : C.border}`, marginBottom: '6px' }}
            >
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ color: C.textPrimary, fontWeight: 600, fontSize: '13px' }}>{c.name}</span>
                {c.scope === 'ENTERPRISE'
                  ? <span style={{ background: C.blue + '22', color: C.blue, padding: '1px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 700 }}>ENTERPRISE</span>
                  : <span style={{ background: C.amber + '22', color: C.amber, padding: '1px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 700 }}>SITE</span>
                }
              </div>
              <div style={{ color: C.textMuted, fontSize: '11px' }}>{c.validityMonths ? `Valid ${c.validityMonths} months` : 'No expiry'}</div>
            </div>
          ))
        }
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={onClose} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSecondary, borderRadius: '6px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleAssign} disabled={saving || selected.length === 0} style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: saving || selected.length === 0 ? 0.5 : 1 }}>
            {saving ? 'Assigning…' : `Assign (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
